import { scoreSet, type FarkleEvent, type FarkleView } from '@campfire/game-farkle';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { die, randomFace, tumbling } from './dice.ts';

const n = (x: number) => x.toLocaleString('en-US');

const eventText = (ctx: TableCtx, e: FarkleEvent | null, v: FarkleView): string => {
  if (!e) return `${name(ctx, v.turn)} ${v.turn === ctx.you ? 'start' : 'starts'}: roll six dice.`;
  const who = name(ctx, e.seat);
  if (e.kind === 'roll') return `${who} rolled ${e.dice.join(' ')}.`;
  if (e.kind === 'farkle') return `FARKLE! ${who} rolled ${e.dice.join(' ')}${e.lost ? ` and lost ${n(e.lost)}` : ''}.`;
  if (e.kind === 'keep') return `${who} set aside ${e.dice.join(' ')} for ${n(e.points)}${e.hot ? ' - hot dice!' : ''}.`;
  return `${who} banked ${n(e.points)} and has ${n(e.total)}.`;
};

export const farkleUi: GameUi = {
  id: 'farkle',
  privateHands: false,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as FarkleView;
    return `${n(v.scores[s] ?? 0)} pts${v.onBoard[s] ? '' : v.entry ? ' · off the board' : ''}`;
  },
  render(ctx) {
    const v = ctx.msg.view as FarkleView;
    const done = v.result !== null;
    // Picked dice live on the table, so everyone sees them as they're chosen (FR-FARKLE-13).
    const sel = v.picked ?? [];
    const lastDice = v.last?.kind === 'roll' || v.last?.kind === 'farkle' ? v.last.dice : [];
    const rolling = tumbling(ctx, 'fk-roll', `${v.turn}-${v.round}-${v.kept.length}-${lastDice.join('')}-${v.last?.kind}`, v.last?.kind === 'roll' || v.last?.kind === 'farkle');
    const keeping = ctx.myTurn && v.phase === 'keep' && !rolling;
    const toggle = (i: number) => ctx.act({ type: 'pick', index: i });
    const preview = sel.length ? scoreSet(sel.map((i) => v.dice[i] as number)) : null;

    const score = h(
      'div.fk-scores',
      { 'data-testid': 'fk-scores' },
      v.scores.map((pts, s) =>
        h(
          `div.fk-score${s === v.turn && !done ? '.active' : ''}${s === v.finalSeat ? '.final' : ''}`,
          { 'data-testid': `fk-score-${s}` },
          h('div.muted', {}, name(ctx, s)),
          h('strong', {}, n(pts)),
          !v.onBoard[s] && v.entry ? h('div.muted', {}, 'off board') : null,
        ),
      ),
    );

    const tableDice = v.dice.length
      ? v.dice.map((d, i) => die(rolling ? randomFace() : d, { selected: sel.includes(i), onclick: keeping ? () => toggle(i) : undefined, testid: `fk-die-${i}` }))
      : v.last?.kind === 'farkle'
        ? v.last.dice.map((d) => die(rolling ? randomFace() : d, { dim: !rolling }))
        : [];

    return h(
      'div.fk-table',
      {},
      score,
      v.finalSeat !== null && !done ? h('div.hint', { 'data-testid': 'fk-final' }, `${name(ctx, v.finalSeat)} reached ${n(v.target)}. Last turns!`) : null,
      h('div.fk-turn', { 'data-testid': 'fk-turn' }, `${name(ctx, v.turn)} - turn total `, h('strong', { 'data-testid': 'fk-turn-total' }, n(v.turnPoints))),
      h(`div.dice-row${rolling ? '.rolling' : ''}`, { 'data-testid': 'fk-dice', 'data-rolling': rolling ? 'true' : undefined }, tableDice),
      v.kept.length
        ? h('div.fk-kept', { 'data-testid': 'fk-kept' }, h('span.muted', {}, 'Set aside '), h('div.dice-row.small', {}, v.kept.map((d) => die(d, { small: true }))))
        : null,
      h('div.event', { 'data-testid': 'event' }, rolling ? `${name(ctx, v.last?.seat ?? v.turn)} is rolling…` : eventText(ctx, v.last, v)),
      ctx.myTurn && !done && !rolling
        ? h(
            'div.row.actions',
            {},
            keeping
              ? h(
                  'button.primary.big',
                  { 'data-testid': 'fk-keep', disabled: preview === null, onclick: () => ctx.act({ type: 'keep' }) },
                  preview === null ? (sel.length ? 'Every die must score' : 'Pick scoring dice') : `Set aside (+${n(preview)})`,
                )
              : [
                  h('button.primary.big', { 'data-testid': 'fk-roll', disabled: rolling, onclick: () => ctx.act({ type: 'roll' }) }, v.phase === 'roll' ? 'Roll 6 dice' : `Roll ${v.toRoll} dice`),
                  v.phase === 'decide'
                    ? h('button.big', { 'data-testid': 'fk-bank', disabled: !v.canBank, onclick: () => ctx.act({ type: 'bank' }) }, v.canBank ? `Bank ${n(v.turnPoints)}` : `Need ${n(v.entry)} to bank`)
                    : null,
                ],
          )
        : null,
    );
  },
};
